import React, { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { FaFileAlt, FaFilePdf, FaFileImage, FaFileVideo, FaPaperclip, FaPlus, FaTrashAlt } from 'react-icons/fa'
import ConfirmDelete from './ConfirmDelete'
import MwSpinnerButton from './MwSpinnerButton'
import { deleteUploadedFiles, getAttachmentInfo, normalizeAttachmentList, uploadSupportFiles } from '../../utils/attachments'

const fileIcon = (info) => {
    if (info.isPdf) return { icon: <FaFilePdf size={18} />, bg: '#fff1f2', color: '#ef4444' };
    if (info.isImage) return { icon: <FaFileImage size={18} />, bg: '#f0fdf4', color: '#16a34a' };
    if (info.isVideo) return { icon: <FaFileVideo size={18} />, bg: '#faf5ff', color: '#9333ea' };
    return { icon: <FaFileAlt size={18} />, bg: '#f0f9ff', color: '#0ea5e9' };
};

// إدارة مرفقات التذكرة / المشروع من صفحة العرض: رفع ملفات جديدة وحذف ملف بعد تأكيد.
// onAdd([{ url, name }]) و onRemove(url) بيحدّثوا السجل على السيرفر؛ الملف نفسه بيترفع / يتمسح هنا.
const AttachmentsManager = ({
    attachments,
    attachmentNames,
    isAr = true,
    title,
    canAdd = true,
    canRemove = true,
    onAdd,
    onRemove,
    onOpen,
    getImageSrc,
    inputId = 'attachments_manager_input',
    compact = false,
}) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const list = normalizeAttachmentList(attachments);
    const busy = uploading || removing;
    const allowRemove = (url) => (typeof canRemove === 'function' ? canRemove(url) : Boolean(canRemove));

    const uploadHandler = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length || !onAdd) return;
        setUploading(true);
        let uploaded = [];
        try {
            uploaded = await uploadSupportFiles(files);
            if (!uploaded.length) throw new Error('empty upload');
            await onAdd(uploaded);
            toast.success(isAr
                ? (uploaded.length > 1 ? `تم رفع ${uploaded.length} ملفات` : 'تم رفع الملف')
                : (uploaded.length > 1 ? `${uploaded.length} files uploaded` : 'File uploaded'));
        } catch {
            // الملف اترفع بس ماتسجلش — نمسحه عشان مايفضلش يتيم على سيرفر الرفع.
            if (uploaded.length) deleteUploadedFiles(uploaded.map((item) => item.url)).catch(() => {});
            toast.error(isAr ? 'فشل رفع الملف، حاول مرة أخرى' : 'Upload failed, please try again');
        } finally {
            setUploading(false);
        }
    };

    const confirmDelete = async () => {
        const url = pendingDelete?.url;
        if (!url || !onRemove) return;
        setRemoving(true);
        try {
            await onRemove(url);
            await deleteUploadedFiles([url]).catch(() => false);
            toast.success(isAr ? 'تم حذف الملف' : 'File deleted');
            setPendingDelete(null);
        } catch {
            toast.error(isAr ? 'تعذر حذف الملف' : 'Failed to delete the file');
        } finally {
            setRemoving(false);
        }
    };

    const openFile = (url) => {
        if (onOpen) onOpen(url);
        else window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1 }}>
                    <FaPaperclip size={11} />
                    <span>{title || (isAr ? 'المرفقات' : 'Attachments')}</span>
                    <span style={{ minWidth: 22, padding: '1px 7px', borderRadius: 999, background: '#eef2ff', color: '#4f46e5', fontSize: 10, fontWeight: 900, textAlign: 'center' }}>{list.length}</span>
                </div>
                {canAdd && onAdd && (
                    <>
                        <input ref={inputRef} id={inputId} type="file" multiple onChange={uploadHandler} disabled={busy} style={{ display: 'none' }} />
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={busy}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', fontSize: 12, fontWeight: 800, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
                        >
                            {uploading ? <MwSpinnerButton withLabel={false} /> : <FaPlus size={10} />}
                            {uploading ? (isAr ? 'جاري الرفع...' : 'Uploading...') : (isAr ? 'إضافة ملف' : 'Add file')}
                        </button>
                    </>
                )}
            </div>

            {!list.length ? (
                <div
                    onClick={() => canAdd && onAdd && !busy && inputRef.current?.click()}
                    style={{ padding: compact ? '12px' : '18px 12px', borderRadius: 12, border: '1.5px dashed #e2e8f0', background: '#f8fafc', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#94a3b8', cursor: canAdd && onAdd ? 'pointer' : 'default' }}
                >
                    {canAdd && onAdd
                        ? (isAr ? 'لا توجد مرفقات — اضغط لإضافة ملف' : 'No attachments — click to add a file')
                        : (isAr ? 'لا توجد مرفقات' : 'No attachments')}
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
                    {list.map((url) => {
                        const info = getAttachmentInfo(url, attachmentNames);
                        const meta = fileIcon(info);
                        const removable = Boolean(onRemove) && allowRemove(url);
                        return (
                            <div
                                key={url}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 12, background: '#f8fafc', border: '1.5px solid #e2e8f0', minWidth: 0 }}
                            >
                                <button
                                    type="button"
                                    onClick={() => openFile(url)}
                                    title={info.name}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'start' }}
                                >
                                    {info.isImage ? (
                                        <img src={getImageSrc ? getImageSrc(url) : url} alt={info.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8, flexShrink: 0, border: '1px solid #e2e8f0' }} />
                                    ) : (
                                        <span style={{ width: 40, height: 40, borderRadius: 8, flexShrink: 0, background: meta.bg, color: meta.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {meta.icon}
                                        </span>
                                    )}
                                    <span style={{ minWidth: 0 }}>
                                        <span dir="auto" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.name}</span>
                                        <span style={{ display: 'block', fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                                            {info.ext || (isAr ? 'ملف' : 'file')} · {isAr ? 'انقر للفتح' : 'Click to open'}
                                        </span>
                                    </span>
                                </button>
                                {removable && (
                                    <button
                                        type="button"
                                        onClick={() => setPendingDelete(info)}
                                        disabled={busy}
                                        title={isAr ? 'حذف الملف' : 'Delete file'}
                                        style={{ width: 30, height: 30, borderRadius: 999, flexShrink: 0, border: '1px solid #fecaca', background: '#fff1f2', color: '#ef4444', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}
                                    >
                                        <FaTrashAlt size={11} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <ConfirmDelete
                title={isAr ? 'حذف ملف' : 'Delete file'}
                msg={isAr
                    ? `هل أنت متأكد من حذف الملف "${pendingDelete?.name || ''}"؟ لا يمكن التراجع عن هذا الإجراء.`
                    : `Delete the file "${pendingDelete?.name || ''}"? This cannot be undone.`}
                onShow={Boolean(pendingDelete)}
                setOnShow={(show) => { if (!show) setPendingDelete(null); }}
                loading={removing}
                onDeleteHandler={confirmDelete}
                confirmLabel={isAr ? 'حذف الملف' : 'Delete file'}
            />
        </div>
    );
};

export default AttachmentsManager;
